import { AlertColor, Divider, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import strings from '../../../../../../common/Translation/Translate';
import Notification from '../../../../../../components/Notification';
import axiosInstance from '../../../../../../utils/axios';
import { routes } from '../../../../../../utils/constants/routes';
import { URLS } from '../../../../../../utils/constants/urls';
import { fetchIdsFromSelectedDropdownValues } from '../../../../../../utils/validators/HelperFunctions';
import AddEdit from '../../../../CommonPage/AddEdit';
import { CategorizedAddonSelector } from '../../CategorizedAddonSelector';
import { AddEditVenueConfigTimeslots } from './AddEditVenueConfigTimeslots/AddEditVenueConfigTimeslots';
import { AddEditMappingLayout } from './AddEditMappingLayout/AddEditMappingLayout';
import { AddEditTimeSlotMinimumRevenue } from './AddEditTimeSlotMinimumRevenue/AddEditTimeSlotMinimumRevenue';
import {
  buildTimeslotMinimumRevenuesPayload,
  buildVenueConfigTimeslotsPayload,
  mapDefaultVenueConfigTimeslotsFromDefaults,
  mapTimeslotMinimumRevenuesFromApi,
  mapVenueConfigTimeslotsFromApi,
  TimeslotMinimumRevenueRow,
  validateTimeslotMinimumRevenues,
  validateVenueConfigTimeslots,
  VenueConfigTimeslotRow,
} from './AddEditTimeSlotMinimumRevenue/timeslotFormUtils';
import { extractVenueConfigAddonIds } from './mappingFormUtils';
type MappingProps = {
  dropdownData?: any;
  pageResponse: any;
  getPageData: (id: number) => void;
};

const Mapping: FC<MappingProps> = ({ dropdownData, pageResponse, getPageData }) => {
  const { id } = useParams() as any;
  const Navigate = useNavigate();
  const queryStings = useLocation().search as any;
  const queryStingId = new URLSearchParams(queryStings).get('id') as string;
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<number[]>([]);
  const [layout, setLayout] = useState<any>([]);
  const [venueConfigTimeslots, setVenueConfigTimeslots] = useState<VenueConfigTimeslotRow[]>([]);
  const [defaultTimeslots, setDefaultTimeslots] = useState<VenueConfigTimeslotRow[]>([]);
  const [revenueRequests, setRevenueRequests] = useState<TimeslotMinimumRevenueRow[]>([]);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const loadConfigurationDefaults = useCallback(async () => {
    const { data } = await axiosInstance.get(URLS.venueConfigDefaults);
    setDefaultTimeslots(mapDefaultVenueConfigTimeslotsFromDefaults(data));
  }, []);

  useEffect(() => {
    loadConfigurationDefaults();
  }, [loadConfigurationDefaults]);

  useEffect(() => {
    if (id || queryStingId) {
      setLayout(pageResponse.layouts ?? []);
      const mappedTimeslots = mapVenueConfigTimeslotsFromApi(pageResponse.venueConfigTimeslots ?? []);
      setVenueConfigTimeslots(mappedTimeslots.length > 0 ? mappedTimeslots : defaultTimeslots);
      setRevenueRequests(mapTimeslotMinimumRevenuesFromApi(pageResponse.timeslotMinimumRevenues ?? []));
      setSelectedAddOnIds(extractVenueConfigAddonIds(pageResponse.addOns));
    } else if (defaultTimeslots.length > 0) {
      setVenueConfigTimeslots(prev => (prev.length > 0 ? prev : defaultTimeslots));
    }
    // eslint-disable-next-line
  }, [
    pageResponse.id,
    pageResponse.layouts,
    pageResponse.addOns,
    pageResponse.venueConfigTimeslots,
    pageResponse.timeslotMinimumRevenues,
    defaultTimeslots,
    id,
    queryStingId,
  ]);

  const pageConfig = React.useMemo(() => {
    return {
      title: id ? strings.common_button_update : strings.common_button_save,
      successMessage: id ? strings.common_update_message : strings.common_save_message,
      addEditApiUrl:
        id || queryStingId ? URLS.venueConfigsUpdateMapping(id || queryStingId) : URLS.addVenueConfiguration,
      initialApi: URLS.get_edit_delete_VenueById(id || queryStingId),
      formModel: [],
      navigateToPage: ``,
      isCustomResponse: true,
      queryStingId: queryStingId,
      hideClose: true,
    };
  }, [dropdownData?.addOns?.length, id, queryStingId]);

  const handleCustomResponse = () => {
    const timeslotError = validateVenueConfigTimeslots(venueConfigTimeslots);
    if (timeslotError) throw new Error(timeslotError);
    const revenueError = validateTimeslotMinimumRevenues(revenueRequests);
    if (revenueError) throw new Error(revenueError);
    return {
      addOnIds: selectedAddOnIds,
      layouts:
        layout?.map((item: any) => ({
          ...item,
          layoutId: item.layouts ? item.layouts?.value : item.layoutId,
          layoutName: item.layouts ? item.layouts?.label : item.layoutName,
        })) || [],
      venueConfigTimeslots: buildVenueConfigTimeslotsPayload(venueConfigTimeslots),
      revenueRequests: buildTimeslotMinimumRevenuesPayload(revenueRequests),
    };
  };

  const customCallbackResponse = (callbackValue: any) => {
    if (!id) {
      Navigate(`${routes.addVenueConfiguration}?id=${callbackValue.id}`);
    }
    getPageData(callbackValue.id);
  };

  const defaultTimeslotRow = defaultTimeslots[0];

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <AddEdit
        dropdownValue={dropdownData}
        customCallbackResponse={customCallbackResponse}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        handleCustomResponse={handleCustomResponse as any}>
        <AddEditVenueConfigTimeslots
          editedData={venueConfigTimeslots}
          setData={setVenueConfigTimeslots}
          defaultTimeslotRow={defaultTimeslotRow}
        />
        <Divider sx={{ my: 2 }} />
        <AddEditTimeSlotMinimumRevenue editedData={revenueRequests} setData={setRevenueRequests} />
        <Divider sx={{ my: 2 }} />
        <AddEditMappingLayout
          editedData={layout}
          setData={setLayout}
          dropdownValue={dropdownData}
          layoutItems={dropdownData?.layoutItems ?? []}
        />
        <Divider sx={{ my: 2 }} />
        <CategorizedAddonSelector
          addOnItems={dropdownData?.addOnItems ?? []}
          selectedIds={selectedAddOnIds}
          onChange={setSelectedAddOnIds}
        />
      </AddEdit>
    </>
  );
};
export default Mapping;
